import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MantineProvider, MantineThemeOverride, useMantineTheme, createTheme, rem, CSSVariablesResolver } from '@mantine/core'
import { Notifications, notifications } from '@mantine/notifications'
import '@mantine/notifications/styles.css'
import { ModalsProvider } from '@mantine/modals'
//import { ModalSettings } from '@mantine/modals/lib/context'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
//import "@/core/i18nConfig"
import '@mantine/core/styles.css'

// styles for mantien date picker
import '@/styles/app.css'
import { UserProvider } from '@/context/UserContext'
import PasswordReset from '@/components/auth/PasswordReset'
import api from '@/core/api'
import { AuthModalProvider } from '@/context/AuthModalContext'
import ScrollToTop from '@/components/common/ScrollToTop'
import mixpanel from '@/utils/mixpanel'
import { useLocation } from 'react-router-dom'

import Routes from './routes'
import { isAuthError } from './constants/errors'
import { showErrorNotification } from './constants/notifications'

import ReactGA from 'react-ga4'
ReactGA.initialize('G-QBNB7XS5P4', {
  testMode: import.meta.env.VITE_IS_PROD !== 'true'
})

// default operation on errors
const onError = (error: any) => {
  showErrorNotification(error.message)

  if (error?.code && isAuthError(error.code)) {
    // First, force a logout to clear the session token
    api.auth.logout().catch(console.error)

    // Then clear query cache
    queryClient.clear()

    // Force a user check
    queryClient.invalidateQueries(['user'])

    // Reload page
    window.location.reload()
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      cacheTime: 1000 * 60 * 60 * 24 // 24 hours
    },
    mutations: {
      retry: 0
    }
  },
  mutationCache: new MutationCache({
    onError
  }),
  queryCache: new QueryCache({
    onError
  })
})

// Create a persister for localStorage
const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'etf-nexus-cache'
})

const cssVariablesResolver: CSSVariablesResolver = (theme) => ({
  variables: {},
  light: {
    // '--mantine-color-text': theme.colors.grey[8],
    '--text-color': theme.colors.grey[8],
    '--mantine-color-placeholder': theme.colors.grey[6],
    '--background-gradient': 'linear-gradient(0deg, #FFF 89.81%, #F5FAFF 100%)'
  },
  dark: {}
})

const mainTheme: MantineThemeOverride = createTheme({
  breakpoints: {
    sm: '375px', // will probably just use 'base' instead of sm
    md: '744px', // tablet
    lg: '992px', // desktop
    xl: '1280px' // desktop with sidebar ads
  },
  spacing: {
    '4xs': rem(3),
    '3xs': rem(6),
    '2xs': rem(8),
    xs: rem(10),
    sm: rem(12),
    smmd: rem(14),
    md: rem(16),
    mdlg: rem(20),
    lg: rem(24),
    xl: rem(32),
    '2xl': rem(48),
    '3xl': rem(64),
    '4xl': rem(80),
    '5xl': rem(109)
  },
  radius: {
    '3xs': rem(6),
    '2xs': rem(8),
    xs: rem(10),
    sm: rem(12),
    md: rem(16)
  },
  fontSizes: {
    xs: rem(12),
    sm: rem(14),
    md: rem(16),
    lg: rem(20),
    xl: rem(26),
    '2xl': rem(32),
    '3xl': rem(42)
  },
  colors: {
    blue: [
      '#EFF5FB', // 0 - Lightest - Blue/50 - secondary
      '#E7F0F9', // 1 - Blue/100 - hover sec
      '#BFDFFF', // 2 - Blue/200 - hover sec
      '#447AB1', // 3 - Blue/300 - text on dark bg
      '#054484', // 4 - Blue/400 - hover prim
      '#00386F', // 5 - Blue/500 - primary
      '#002E5B', // 6 - Blue/600 - pressed prim
      '#00172E', // 7 - Blue/700 - footer
      '#00172E', // 8 - Duplicate
      '#00172E' // 9 - Duplicate
    ],
    grey: [
      '#FCFCFD', // 0 - Lightest - Grey/0 - forms bg
      '#F9FAFB', // 1 - Grey/50 - sections bg
      '#F3F5F7', // 2 - Grey/100 - chips bg
      '#EEF0F2', // 3 - Grey/200 - dividers
      '#E0E5EB', // 4 - Grey/300 - chart grid
      '#C8CFD5', // 5 - Grey/400 - checkbox outline
      '#8E99A4', // 6 - Grey/500 - placeholder text
      '#717E8A', // 7 - Grey/600 - secondary text
      '#292D30', // 8 - Grey/800 - text
      '#292D30' // 9 - Duplicate
    ],
    red: [
      '#FBF3F5', // 0 - Lightest - Red/100 - neg. bg
      '#F9E7EB', // 1 - Red/200 - neg. hover
      '#F9E7EB', // 2 - Duplicate
      '#FF4C58', // 3 - Red/400 neg. dark bg
      '#F5003D', // 4 - Red/500 - negative //
      '#F5003D', // 5 - Duplicate //
      '#F5003D', // 6 - Duplicate //
      '#F5003D', // 7 - Duplicate
      '#F5003D', // 8 - Duplicate
      '#F5003D' // 9 - Duplicate
    ],
    green: [
      '#F3FDFA', // 0 - Lightest - Green/100 - pos. bg
      '#F3FDFA', // 1 - Duplicate
      '#F3FDFA', // 2 - Duplicate
      '#F3FDFA', // 3 - Duplicate
      '#00A875', // 4 - Green/500 - positive
      '#009C6F', // 5 - Green/600
      '#009C6F', // 6 - Duplicate
      '#009C6F', // 7 - Duplicate
      '#009C6F', // 8 - Duplicate
      '#009C6F' // 9 - Duplicate
    ],
    white: [
      '#FFFFFF', // 0 - White
      '#FFFFFF', // 1 - Duplicate
      '#FFFFFF', // 2 - Duplicate
      '#FFFFFF', // 3 - Duplicate
      '#FFFFFF', // 4 - Duplicate
      '#FFFFFF', // 5 - Duplicate
      '#FFFFFF', // 6 - Duplicate
      '#FFFFFF', // 7 - Duplicate
      '#FFFFFF', // 8 - Duplicate
      '#FFFFFF' // 9 - Duplicate
    ],
    golden: [
      '#FFB40A', // 0 - Golden - stars
      '#FFB40A', // 1 - Duplicate
      '#FFB40A', // 2 - Duplicate
      '#FFB40A', // 3 - Duplicate
      '#FFB40A', // 4 - Duplicate
      '#FFB40A', // 5 - Duplicate
      '#FFB40A', // 6 - Duplicate
      '#FFB40A', // 7 - Duplicate
      '#FFB40A', // 8 - Duplicate
      '#FFB40A' // 9 - Duplicate
    ]
  },
  primaryColor: 'blue',
  fontFamily: 'IBM Plex Sans, sans-serif',
  headings: {
    fontFamily: 'IBM Plex Sans, sans-serif',
    sizes: {
      h1: {
        fontSize: rem(32)
      },
      h2: {
        fontSize: rem(24),
        fontWeight: '700'
      },
      h3: {
        fontWeight: '700',
        fontSize: rem(20)
      }
    }
  },
  components: {
    Badge: {
      styles: {
        root: {
          textTransform: 'none'
        }
      }
    },
    Button: {
      styles: (theme, params) => {
        let backgroundColor, color, hoverBackgroundColor

        if (params.variant === 'subtle') {
          backgroundColor = theme.colors.blue[0]
          color = theme.colors.blue[5]
          hoverBackgroundColor = theme.colors.blue[0]
        } else if (params.variant === 'transparent') {
          backgroundColor = 'transparent'
          color = theme.colors.grey[8]
          hoverBackgroundColor = 'initial'
        } else if (params.variant === 'red') {
          backgroundColor = theme.colors.red[0]
          color = theme.colors.red[4]
          hoverBackgroundColor = theme.colors.red[0]
        } else if (params.variant === 'disabled') {
          backgroundColor = theme.colors.grey[3]
          color = theme.colors.grey[6]
          hoverBackgroundColor = theme.colors.grey[3]
        } else {
          backgroundColor = theme.colors.blue[5]
          color = theme.white[0]
          hoverBackgroundColor = theme.colors.blue[5]
        }

        return {
          root: {
            fontSize: theme.fontSizes.md,
            fontWeight: 600,
            backgroundColor,
            borderRadius: theme.radius.xs,
            color,
            '&:hover': {
              backgroundColor: hoverBackgroundColor
            }
          }
        }
      }
    },
    Input: {
      styles: (theme) => ({
        input: {
          borderRadius: 8
        }
      })
    },
    TextInput: {
      styles: (theme) => ({
        input: {
          color: theme.colors.grey[8],
          '&::placeholder': {
            color: theme.colors.grey[6]
          }
        }
      })
    },
    InputWrapper: {
      styles: (theme) => ({
        label: {
          fontWeight: 500,
          fontSize: theme.fontSizes.sm
        }
      })
    },
    Anchor: {
      styles: (theme, params) => ({
        root: {
          fontWeight: 400,
          textDecoration: 'underline',
          color: theme.colors.grey[8]
        }
      })
    },

    Tabs: {
      styles: (theme) => ({
        tab: {
          '&[data-active]': {
            borderBottom: `5px solid ${theme.colors.blue[5]}`
          },
          '&:not([data-active])': {
            borderBottom: `5px solid ${theme.colors.grey[2]}`
          }
        }
      })
    }
  }
})

const getModalProps = () /*: ModalSettings*/ => {
  return {
    closeOnClickOutside: true,
    withCloseButton: true,
    overlayProps: {
      /*color: theme.colorScheme === 'dark' ? theme.colors.grey[9] : theme.colors.gray[2],*/
      opacity: 0.55,
      blur: 3
    }
  }
}

// Custom hook to track page views
function useMixpanelPageView() {
  const location = useLocation()
  useEffect(() => {
    mixpanel.track('Page View', { page: location.pathname })
  }, [location])
}

// Tracker component to use the hook inside Router
function MixpanelPageViewTracker() {
  useMixpanelPageView()
  return null
}

// Google Analytics component
function GAPageViewTracker() {
  const location = useLocation()

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
      title: document.title
    })
  }, [location])

  return null
}

const App = () => {
  /* const { user } = useUserStore()
  if (user?.defaultLanguage) {
    i18n.changeLanguage(user?.defaultLanguage)
  }*/ // DISABLE PARSE

  return (
    <AuthModalProvider>
      <React.StrictMode>
        <MantineProvider theme={mainTheme} cssVariablesResolver={cssVariablesResolver}>
          <Notifications position="top-center" />
          <ModalsProvider modalProps={getModalProps()}>
            <PersistQueryClientProvider
              client={queryClient}
              persistOptions={{
                persister: localStoragePersister,
                maxAge: 1000 * 60 * 60 * 24 // 24 hours
              }}>
              <BrowserRouter>
                <ScrollToTop />
                <UserProvider>
                  <PasswordReset />
                  <MixpanelPageViewTracker />
                  <GAPageViewTracker />
                  <Routes />
                </UserProvider>
              </BrowserRouter>
            </PersistQueryClientProvider>
          </ModalsProvider>
        </MantineProvider>
      </React.StrictMode>
    </AuthModalProvider>
  )
}

const rootElem = document.getElementById('root')
if (rootElem) {
  ReactDOM.createRoot(rootElem).render(<App />)
}
